import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NavigationMenuItem } from "@/src/components/Chat/ContentNavigationMenu/types";

export interface UseMarkdownNavigationOptions {
  content?: string;
  currentIndex?: number; // 添加currentIndex参数用于区分唯一性
}

export interface UseMarkdownNavigationReturn {
  navigationItems: NavigationMenuItem[];
  activeItemId: string | undefined;
  handleItemClick: (itemId: string) => void;
}

export const useMarkdownNavigation = ({
  content,
  currentIndex = 0, // 添加currentIndex参数，默认值为0
}: UseMarkdownNavigationOptions): UseMarkdownNavigationReturn => {
  const [activeItemId, setActiveItemId] = useState<string | undefined>();
  const userClickRef = useRef(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  // 统一的元素查找函数
  const findTargetElement = useCallback(
    (index: number): Element | null => {
      try {
        const scrollContainer = document.getElementById("container");
        if (!scrollContainer) {
          return null;
        }

        // 优先查找已设置索引的元素，使用包含currentIndex的属性
        let target = scrollContainer.querySelector(
          `[data-heading-index="${currentIndex}-${index}"]`,
        );

        // 回退到按序查找，只查找当前currentIndex对应的元素
        if (!target) {
          const allH2s = scrollContainer.querySelectorAll(
            `[data-current-index="${currentIndex}"]`,
          );
          target = allH2s?.[index] as Element;
        }

        return target;
      } catch (error) {
        return null;
      }
    },
    [currentIndex],
  );

  const navigationItems = useMemo<NavigationMenuItem[]>(() => {
    if (!content) {
      return [];
    }

    const h2Orh3Regex = /^##[#]? (.+)$/gm;
    const matches = Array.from(content.matchAll(h2Orh3Regex));

    return matches.map((match, index) => ({
      id: `heading-${currentIndex}-${index}`, // 使用currentIndex作为前缀
      label: match[1].trim().replace(/\*\*/g, ""), // 去除星号
    }));
  }, [content, currentIndex]);

  // 默认选中第一个导航项
  useEffect(() => {
    if (navigationItems.length > 0 && !activeItemId) {
      setActiveItemId(`heading-${currentIndex}-0`); // 使用currentIndex作为前缀
    }
  }, [navigationItems, activeItemId, currentIndex]);

  const handleItemClick = useCallback(
    (itemId: string) => {
      // 设置用户点击标志，防止被自动滚动更新覆盖
      userClickRef.current = true;

      // 清除之前的定时器
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      // 解析itemId获取索引，格式为 heading-{currentIndex}-{index}
      const index = parseInt(itemId.split("-").pop() || "0");
      const targetElement = findTargetElement(index);

      if (targetElement) {
        // 查找 id="container" 的滚动容器
        const scrollContainer = document.getElementById("container");
        if (scrollContainer) {
          // 容器内滚动
          const containerRect = scrollContainer.getBoundingClientRect();
          const elementRect = targetElement.getBoundingClientRect();
          const currentScrollTop = scrollContainer.scrollTop;

          // 计算目标元素相对于容器的位置
          const elementTopRelativeToContainer =
            elementRect.top - containerRect.top;

          // 计算目标滚动位置：当前滚动位置 + 元素相对位置 - 60px
          const targetScrollTop =
            currentScrollTop + elementTopRelativeToContainer - 60;

          // 滚动容器到目标位置
          scrollContainer.scrollTo({
            top: targetScrollTop,
            behavior: "smooth",
          });
        }

        setActiveItemId(itemId);

        // 设置新的定时器，延迟重置用户点击标志
        scrollTimeoutRef.current = setTimeout(() => {
          userClickRef.current = false;
        }, 1000);
      } else {
        userClickRef.current = false; // 如果没找到元素，立即重置
      }
    },
    [findTargetElement],
  );

  // 滚动联动功能 - DOM 索引设置 + 观察器
  useEffect(() => {
    try {
      const scrollContainer = document.getElementById("container");
      if (!scrollContainer || navigationItems.length === 0) {
        return;
      }

      // 查找所有 h2 元素并设置索引，只处理当前currentIndex对应的元素
      const headingElements = scrollContainer.querySelectorAll(
        `[data-current-index="${currentIndex}"]`,
      );

      if (headingElements.length === 0) {
        return;
      }

      // 按 DOM 顺序设置索引，使用包含currentIndex的属性
      headingElements.forEach((element, index) => {
        try {
          element.setAttribute(
            "data-heading-index",
            `${currentIndex}-${index}`,
          );
        } catch (error) {
          // 静默处理错误
        }
      });

      const observer = new IntersectionObserver(
        (entries) => {
          try {
            // 如果是用户点击触发的滚动，不自动更新 activeItemId
            if (userClickRef.current) {
              return;
            }

            const visibleEntries = entries.filter(
              (entry) => entry.isIntersecting,
            );
            if (visibleEntries.length > 0) {
              // 使用简化的距离计算找到最接近理想位置的元素
              const containerRect = scrollContainer.getBoundingClientRect();
              const idealPosition = containerRect.top + 60; // 理想位置：容器顶部下方60px

              const closestEntry = visibleEntries.reduce((closest, entry) => {
                const currentDistance = Math.abs(
                  entry.boundingClientRect.top - idealPosition,
                );
                const closestDistance = Math.abs(
                  closest.boundingClientRect.top - idealPosition,
                );
                return currentDistance < closestDistance ? entry : closest;
              });

              const headingIndex =
                closestEntry.target.getAttribute("data-heading-index");
              if (headingIndex) {
                setActiveItemId(`heading-${headingIndex}`); // headingIndex已经包含currentIndex前缀
              }
            }
          } catch (error) {
            // 静默处理错误
          }
        },
        {
          root: scrollContainer, // 使用容器作为观察根
          rootMargin: "-60px 0px -70% 0px", // 根据容器调整边距
          threshold: [0, 0.1, 0.5], // 多阈值检测
        },
      );

      // 观察设置了索引的元素，只处理当前currentIndex对应的元素
      const indexedElements = scrollContainer.querySelectorAll(
        `[data-heading-index^="${currentIndex}-"]`,
      );

      indexedElements.forEach((element) => {
        try {
          observer.observe(element);
        } catch (error) {
          // 静默处理错误
        }
      });

      return () => {
        try {
          observer.disconnect();
        } catch (error) {
          // 静默处理错误
        }
      };
    } catch (error) {
      // 静默处理错误
    }
  }, [navigationItems, content]); // 监听内容变化

  return {
    navigationItems,
    activeItemId,
    handleItemClick,
  };
};
